// @flow

import * as React from 'react';
import getAppConfig from '../constants/getAppConfig';
import getAuth, { Auth } from '../modules/Auth';

type ChildProps = {
  authReady: boolean,
  user: any,
  auth: Auth,
  authFail: boolean,
  authRetry: () => void,
};

type Props = {
  children: (childProps: ChildProps) => React.Node,
};

const AUTH_RETRIES = 3;
const AUTH_RETRY_DELLAY = 1000;

const Authenticate = ({ children }: Props) => {
  const retriesLeftRef = React.useRef(AUTH_RETRIES);
  const timeoutRetryId = React.useRef(null);
  const [user, setUser] = React.useState(undefined);
  const [cookieReady, setCookieReady] = React.useState(false);
  const [authFail, setAuthFail] = React.useState(false);
  const auth = getAuth();
  const authRetry = () => {
    retriesLeftRef.current = AUTH_RETRIES;
    setAuthFail(false);
    auth.silentLogin();
  };

  React.useEffect(() => {
    if (getAppConfig().toggles.useMockBff) {
      setUser(({
        profile: { sub: 'DUMMY_VALUE' },
        expired: false,
      }));
      setCookieReady(true);
      return;
    }

    auth.addPublicSilentLoginFunction();
    auth.silentLogin(true).catch(() => auth.redirectToLogin());
    auth.getUser().then(u => {
      if (u) setUser(u);
    });
    auth.on('userLoaded', u => {
      setUser(u);
    });
    auth.on('cookieSet', () => {
      setCookieReady(true);
    });
    auth.on(
      'authFail',
      /* istanbul ignore next */ () => {
        if (retriesLeftRef.current > 1) {
          timeoutRetryId.current = setTimeout(() => {
            retriesLeftRef.current -= 1;
            auth.silentLogin();
          }, AUTH_RETRY_DELLAY);
        } else {
          clearTimeout(timeoutRetryId.current);
          setAuthFail(true);
          auth.silentLogin(true).catch(() => auth.redirectToLogin());
        }
      }
    );
  }, [auth]);

  const authReady = !!(user && !user.expired && cookieReady && !authFail);
  return children({ authReady, auth, user, authFail, authRetry });
};

export default Authenticate;
